import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import AlertBox from '@/components/AlertBox';
import { RootState } from '@/redux/store';

import WithInvoice from './companyData/withInvoice';
import WithoutInvoice from './companyData/withoutInvoice';
import PersonalData from './personalData/personalData';
import Services from './services/services';
import useEditProfile from './useEditProfile';

// import Password from './password/password';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export interface IEditProfileProps {
  openCompany?: boolean;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`${index}`} {...other}>
      {value === index && <Box sx={{ p: '1.5rem 0' }}>{children}</Box>}
    </div>
  );
}

const EditProfile = ({ openCompany }: IEditProfileProps) => {
  const hook = useEditProfile({ openCompany });
  const theme = useTheme();
  const { profile } = useSelector((state: RootState) => state.parcel);
  const country = profile.data?.country || 'MEX';
  const isSecondaryOrg =
    profile.data?.organization?.type && profile.data.organization.type !== 'principal';
  const isDependentBilling = profile.data?.company?.billingType === 'DEPENDENT';
  const shouldDisableEditing = Boolean(isSecondaryOrg || isDependentBilling);

  return (
    <Box sx={{ paddingTop: '1rem' }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          width: '100%',
        }}
      >
        <Tabs
          value={hook.value}
          onChange={hook.handleChange}
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ width: '35vw', [theme.breakpoints.down('lg')]: { width: '80vw' } }}
        >
          {[
            { label: 'Personales', id: '0' },
            { label: 'De empresa', id: '1' },
            // { label: 'Contraseña', id: '2' },
            { label: 'Servicios', id: '2' },
          ].map((tab) => (
            <Tab label={tab.label} id={tab.id} />
          ))}
        </Tabs>
      </Box>
      <CustomTabPanel value={hook.value} index={0}>
        <PersonalData
          register={hook.register}
          errors={hook.errors}
          control={hook.control}
          submitForm={hook.submitForm}
          handleChangeSelectedCountryPhone={hook.handleChangeSelectedCountryPhone}
        />
      </CustomTabPanel>
      <CustomTabPanel value={hook.value} index={1}>
        <Grid container spacing={1}>
          {shouldDisableEditing && (
            <Grid size={{ xxs: 12 }} marginBottom={2}>
              <AlertBox type="warning" direction="row">
                <Typography color={theme.palette.grey[700]} fontWeight="bold">
                  Los datos de facturación no pueden ser editados porque{' '}
                  {isSecondaryOrg
                    ? 'perteneces a una organización secundaria'
                    : 'tu tipo de facturación es dependiente'}
                  . Estos datos dependen de la organización principal.
                </Typography>
              </AlertBox>
            </Grid>
          )}
          {country === 'MEX' && (
            <Grid size={{ xxs: 12 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-around"
                flexDirection={hook.isMobile ? 'column' : 'row'}
                sx={{
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: '16px',
                  padding: '0.5rem',
                }}
              >
                <Typography variant="body1" fontWeight={theme.typography.fontWeightMedium}>
                  ¿Necesitas factura?
                </Typography>
                <Controller
                  name="company.requireInvoice"
                  control={hook.control}
                  render={({ field }) => (
                    <FormControl
                      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                    >
                      <Typography variant="body2" fontWeight={theme.typography.fontWeightMedium}>
                        No
                      </Typography>
                      <Switch
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={shouldDisableEditing}
                      />
                      <Typography variant="body2" fontWeight={theme.typography.fontWeightMedium}>
                        Sí
                      </Typography>
                    </FormControl>
                  )}
                />
              </Box>
            </Grid>
          )}
          {country !== 'MEX' ||
          (typeof hook?.watch()?.company === 'object' &&
            'requireInvoice' in (hook?.watch()?.company ?? {}) &&
            (hook?.watch()?.company as { requireInvoice?: boolean }).requireInvoice) ? (
            <>
              <WithInvoice
                register={hook.register}
                errors={hook.errors}
                control={hook.control}
                submitForm={hook.submitForm}
                getValues={hook.getValues}
                setValue={hook.setValue}
                watch={hook.watch}
                shouldDisableEditing={shouldDisableEditing}
              />
            </>
          ) : (
            <WithoutInvoice
              register={hook.register}
              errors={hook.errors}
              control={hook.control}
              submitForm={hook.submitForm}
              shouldDisableEditing={shouldDisableEditing}
            />
          )}
        </Grid>
      </CustomTabPanel>
      {/* <CustomTabPanel value={hook.value} index={2}>
        <Password />
      </CustomTabPanel> */}
      <CustomTabPanel value={hook.value} index={2}>
        <Services />
      </CustomTabPanel>
    </Box>
  );
};

export default EditProfile;
